import {createContext, useContext, useMemo, useState, useEffect} from 'react'
import type {FetchVenuesResponse as Venue} from '../services'
import type {KegLocation} from '../types'

type DayOfWeek = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday'

interface SelectedVenueContextValue {
  selectedVenue: Venue | null
  setSelectedVenue: (venue: Venue | null) => void
  selectedVenueCollectionInfo: {
    pickupInstructions: string | null
    pickupLocation: KegLocation | null
    timeRange: number[]
    phoneNumber: string | null
    collectionDays: DayOfWeek[] | []
  } | null
}

const SelectedVenueContext = createContext<SelectedVenueContextValue | null>(null)

export function SelectVenueProvider({children}: {children: React.ReactNode}) {
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null)
  const [selectedVenueCollectionInfo, setSelectedVenueCollectionInfo] = useState<
    SelectedVenueContextValue['selectedVenueCollectionInfo']
  >(null)

  useEffect(() => {
    if (selectedVenue) {
      const pickupInstructions = selectedVenue.venueCollectionInfo?.pickupInstruction || null
      const pickupLocation = selectedVenue.venueCollectionInfo?.kegLocation || 'Inside' as KegLocation
      const timeRange = [
        selectedVenue.venueCollectionInfo?.startHour || 12,
        selectedVenue.venueCollectionInfo?.endHour || 18,
      ]
      const phoneNumber = selectedVenue.venueCollectionInfo?.phoneNumber || null
      const {venueCollectionInfo} = selectedVenue
      const openDays: DayOfWeek[] = []
      if (venueCollectionInfo?.venueOpenMonday) openDays.push('Monday' as DayOfWeek)
      if (venueCollectionInfo?.venueOpenTuesday) openDays.push('Tuesday' as DayOfWeek)
      if (venueCollectionInfo?.venueOpenWednesday) openDays.push('Wednesday' as DayOfWeek)
      if (venueCollectionInfo?.venueOpenThursday) openDays.push('Thursday' as DayOfWeek)
      if (venueCollectionInfo?.venueOpenFriday) openDays.push('Friday' as DayOfWeek)

      setSelectedVenueCollectionInfo({
        pickupInstructions: pickupInstructions,
        pickupLocation: pickupLocation,
        timeRange: timeRange,
        collectionDays: openDays,
        phoneNumber: phoneNumber,
      })
    }
  }, [selectedVenue])

  const value = useMemo(
    () => ({selectedVenue, setSelectedVenue, selectedVenueCollectionInfo}),
    [selectedVenue, selectedVenueCollectionInfo]
  )
  return <SelectedVenueContext.Provider value={value}>{children}</SelectedVenueContext.Provider>
}

export function useSelectedVenue() {
  const context = useContext(SelectedVenueContext)
  if (!context) throw new Error('useSelectedVenue must be used within a SelectVenueProvider')
  return context
}
